import { useTheme } from "@emotion/react";
import { faDownload, faPaperPlane } from "@fortawesome/pro-solid-svg-icons";

import { useQuote } from "@@/backoffice/for-providers/quotes/hooks/use-quote";
import { useQueryOrParam } from "@@/shared/use-query-or-param";

import { AppPage } from "@@/pages/app-page";
import { appPageIdFactory } from "@@/pages/page-navigation/page-navigation-context";
import { ButtonPrimary } from "@@/shared/buttons_v2/button-primary";
import { ButtonQuaternaryLight } from "@@/shared/buttons_v2/button-quaternary-light";
import { HorizontalDivider, VerticalDivider } from "@@/shared/dividers";
import { FlexColumn, FlexRow } from "@@/shared/flex-containers";
import { ForEach } from "@@/shared/for-each";
import { Icon } from "@@/shared/icons/icon";
import { LayoutCell } from "@@/shared/layout-cell";
import { LayoutGrid } from "@@/shared/layout-grid";
import { PriceTextBox, TextBox } from "@@/shared/text";
import { DateTextBox, TimeRangeTextBox } from "@@/shared/text/date-text-box";

import { useProvider } from "@@/providers/state/queries/provider/use-provider";
import { ProviderLogo } from "@@/shared/pictures/image";
import type {
    HeadQuoteItem,
    Provider,
    Quote,
    SubBasicItem,
} from "@towni/common";
import { isQuoteId, multiplyPrice, sumPrices } from "@towni/common";

/**
 * Customer-facing page to display a quote
 */
const QuoteDisplayPage = () => {
    //const provider = useProviderFromContext();
    const quoteId = useQueryOrParam("quoteId");
    const theme = useTheme();

    const quoteQuery = useQuote(isQuoteId(quoteId) ? quoteId : undefined);
    const [provider] = useProvider(quoteQuery.data?.providerId);
    if (!quoteQuery.data || !provider) {
        return (
            <FlexColumn
                crossAxis="center"
                mainAxis="center"
                padding={{ all: 20 }}>
                <TextBox text="Laddar offert..." />
            </FlexColumn>
        );
    }

    const quote = quoteQuery.data;

    return (
        <AppPage
            mainAxis="stretch"
            pageContainerCss={{
                width: "100%",
                height: "100%",
                label: "quote-display-page",
                //backgroundColor: theme.colors.orange.asString,
            }}
            pageId={appPageIdFactory(
                "quote-display-page-" + (provider.slug ?? "global"),
            )}
            header={<QuoteDisplayHeader quote={quote} provider={provider} />}>
            <FlexColumn padding={{ all: 20 }}>
                <QuoteInfoSection quote={quote} provider={provider} />
                <VerticalDivider M />
                <QuoteItemsSection quote={quote} provider={provider} />
                <VerticalDivider M />
                <QuoteAcceptanceSection />
            </FlexColumn>
        </AppPage>
    );
};

type Props = {
    quote: Quote;
    provider: Provider;
};

/**
 * Header component for the quote display page
 */
const QuoteDisplayHeader = ({ provider }: Props) => {
    const theme = useTheme();

    return (
        <FlexRow
            fillParentWidth
            padding={{ all: 20 }}
            mainAxis="space-between"
            css={{
                backgroundColor: "white",
                borderBottom: `1px solid ${theme.colors.black.light90.asString}`,
                boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
            }}>
            <FlexRow crossAxis="center">
                <ProviderLogo provider={provider} size={5} />
            </FlexRow>

            <FlexRow>
                <ButtonQuaternaryLight>
                    <Icon icon={faDownload} />
                    <HorizontalDivider S />
                    <TextBox
                        text={{ sv: "Ladda ner PDF", en: "Download PDF" }}
                    />
                </ButtonQuaternaryLight>
            </FlexRow>
        </FlexRow>
    );
};

/**
 * Quote information section with provider and customer details
 */
const QuoteInfoSection = ({ quote, provider }: Props) => {
    const theme = useTheme();

    return (
        <FlexColumn
            css={{
                backgroundColor: "white",
                borderRadius: 8,
                boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                overflow: "hidden",
            }}>
            {/* Quote title and reference */}
            <FlexColumn
                padding={{ all: 20 }}
                css={{
                    backgroundColor: theme.colors.black.light97.asString,
                    borderBottom: `1px solid ${theme.colors.black.light90.asString}`,
                }}>
                <TextBox
                    text={quote.title}
                    size="XL"
                    weight="700"
                    color={theme.colors.black.light10}
                />

                <VerticalDivider XS />

                <FlexRow mainAxis="space-between" crossAxis="center">
                    <TextBox
                        text={{
                            sv: `Referens: #${quote._id}`,
                            en: `Reference: #${quote._id}`,
                        }}
                        size="S"
                        color={theme.colors.black.light40}
                    />

                    <FlexColumn
                        padding={{ all: 12 }}
                        css={{
                            backgroundColor:
                                theme.colors.black.light95.asString,
                            border: `1px solid ${theme.colors.black.light90.asString}`,
                            borderRadius: 4,
                        }}>
                        <TextBox
                            text={{
                                sv: "Offerten är giltig till",
                                en: "Quote valid until",
                            }}
                            size="XS"
                            color={theme.colors.black.light40}
                        />
                        <DateTextBox
                            date={quote.valid.end}
                            size="S"
                            weight="600"
                            color={theme.colors.black.light10}
                        />
                    </FlexColumn>
                </FlexRow>
            </FlexColumn>

            {/* Provider and customer info */}
            <LayoutGrid
                css={{
                    padding: 20,
                    gridTemplateColumns: "1fr 1fr",
                    gap: 40,
                }}>
                <LayoutCell>
                    <TextBox
                        text={{ sv: "Från", en: "From" }}
                        size="S"
                        weight="600"
                        color={theme.colors.black.light40}
                    />

                    <VerticalDivider XS />

                    <TextBox
                        text={provider.meta.title}
                        size="M"
                        weight="600"
                        color={theme.colors.black.light10}
                    />

                    <VerticalDivider XXS />

                    <TextBox
                        text={provider.emails[0].emailAddress}
                        size="S"
                        color={theme.colors.black.light40}
                    />

                    <VerticalDivider XXS />

                    <TextBox
                        text={provider.phones[0].phoneNumber}
                        size="S"
                        color={theme.colors.black.light40}
                    />
                </LayoutCell>

                <LayoutCell>
                    <TextBox
                        text={{ sv: "Till", en: "To" }}
                        size="S"
                        weight="600"
                        color={theme.colors.black.light40}
                    />

                    <VerticalDivider XS />

                    <TextBox
                        text={`${quote.customer.firstName} ${quote.customer.lastName || ""}`}
                        size="M"
                        weight="600"
                        color={theme.colors.black.light10}
                    />

                    <VerticalDivider XXS />

                    <TextBox
                        text={quote.customer.emailAddress}
                        size="S"
                        color={theme.colors.black.light40}
                    />

                    {quote.customer.businessOrgNo && (
                        <>
                            <VerticalDivider XXS />
                            <TextBox
                                text={`Org.nummer: ${quote.customer.businessOrgNo}`}
                                size="S"
                                color={theme.colors.black.light40}
                            />
                        </>
                    )}
                </LayoutCell>
            </LayoutGrid>
        </FlexColumn>
    );
};

/**
 * Quote items section displaying all items in the quote
 */
const QuoteItemsSection = ({ quote }: Props) => {
    const theme = useTheme();

    // Calculate totals
    const totalQuote = sumPrices(
        (quote.items ?? []).flatMap(item => {
            return item.subItems.map(subItem => {
                return multiplyPrice(subItem.pricePerItem, subItem.quantity);
            });
        }),
    );

    return (
        <FlexColumn
            css={{
                backgroundColor: "white",
                borderRadius: 8,
                boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                overflow: "hidden",
            }}>
            {/* Header row */}
            <FlexRow
                padding={{ all: 16 }}
                css={{
                    backgroundColor: theme.colors.black.light97.asString,
                    borderBottom: `1px solid ${theme.colors.black.light90.asString}`,
                }}>
                <FlexColumn css={{ flex: 4 }}>
                    <TextBox
                        text={{ sv: "Beskrivning", en: "Description" }}
                        size="S"
                        weight="600"
                        color={theme.colors.black.light40}
                    />
                </FlexColumn>

                <FlexColumn css={{ flex: 1, textAlign: "right" }}>
                    <TextBox
                        text={{ sv: "Antal", en: "Quantity" }}
                        size="S"
                        weight="600"
                        color={theme.colors.black.light40}
                    />
                </FlexColumn>

                <FlexColumn css={{ flex: 1, textAlign: "right" }}>
                    <TextBox
                        text={{ sv: "Pris", en: "Price" }}
                        size="S"
                        weight="600"
                        color={theme.colors.black.light40}
                    />
                </FlexColumn>

                <FlexColumn css={{ flex: 1, textAlign: "right" }}>
                    <TextBox
                        text={{ sv: "Totalt", en: "Total" }}
                        size="S"
                        weight="600"
                        color={theme.colors.black.light40}
                    />
                </FlexColumn>
            </FlexRow>

            {/* Item groups */}
            <ForEach itemOf={quote.items ?? []}>
                {(item: HeadQuoteItem) => {
                    return (
                        <FlexColumn>
                            {/* Group header */}
                            <FlexRow
                                padding={{ all: 16 }}
                                css={{
                                    backgroundColor:
                                        theme.colors.black.light97.asString,
                                    borderBottom: `1px solid ${theme.colors.black.light95.asString}`,
                                }}>
                                <FlexColumn css={{ flex: 3 }}>
                                    <TextBox
                                        text={item.meta.title}
                                        size="M"
                                        weight="600"
                                        color={theme.colors.black.light10}
                                    />
                                </FlexColumn>

                                <FlexColumn
                                    css={{ flex: 3, textAlign: "right" }}>
                                    <TimeRangeTextBox
                                        color={theme.colors.black.light40}
                                        timeRange={item.time}
                                        size="S"
                                    />
                                </FlexColumn>
                            </FlexRow>

                            {/* Sub-items */}
                            <ForEach itemOf={item.subItems}>
                                {(subItem: SubBasicItem) => {
                                    const itemTotal = multiplyPrice(
                                        subItem.pricePerItem,
                                        subItem.quantity,
                                    );

                                    // Skip resource items in display to customers
                                    if (subItem.type === "RESOURCE") {
                                        return null;
                                    }
                                    //Bookable parent row not needed
                                    if (subItem.type === "BOOKABLE") {
                                        return null;
                                    }

                                    return (
                                        <FlexRow
                                            padding={{ all: 16 }}
                                            css={{
                                                borderBottom: `1px solid ${theme.colors.black.light95.asString}`,
                                                paddingLeft: 16,
                                            }}>
                                            <FlexColumn css={{ flex: 4 }}>
                                                <TextBox
                                                    text={subItem.title}
                                                    size="M"
                                                    color={
                                                        theme.colors.black
                                                            .light10
                                                    }
                                                />
                                            </FlexColumn>

                                            <FlexColumn
                                                css={{
                                                    flex: 1,
                                                    textAlign: "right",
                                                }}>
                                                <TextBox
                                                    text={`${subItem.quantity} st`}
                                                    size="M"
                                                    color={
                                                        theme.colors.black
                                                            .light10
                                                    }
                                                />
                                            </FlexColumn>

                                            <FlexColumn
                                                css={{
                                                    flex: 1,
                                                    textAlign: "right",
                                                }}>
                                                <PriceTextBox
                                                    price={subItem.pricePerItem}
                                                    size="M"
                                                    color={
                                                        theme.colors.black
                                                            .light10
                                                    }
                                                />
                                            </FlexColumn>

                                            <FlexColumn
                                                css={{
                                                    flex: 1,
                                                    textAlign: "right",
                                                }}>
                                                <PriceTextBox
                                                    price={itemTotal}
                                                    size="M"
                                                    color={
                                                        theme.colors.black
                                                            .light10
                                                    }
                                                />
                                            </FlexColumn>
                                        </FlexRow>
                                    );
                                }}
                            </ForEach>
                        </FlexColumn>
                    );
                }}
            </ForEach>

            {/* Totals section */}
            <FlexColumn
                padding={{ all: 16 }}
                css={{
                    borderTop: `1px solid ${theme.colors.black.light90.asString}`,
                }}>
                <FlexRow mainAxis="flex-end">
                    <FlexColumn css={{ width: "300px" }}>
                        <FlexRow
                            mainAxis="space-between"
                            padding={{ bottom: 8 }}>
                            <TextBox
                                text={{ sv: "Delsumma", en: "Subtotal" }}
                                size="S"
                                color={theme.colors.black.light40}
                            />
                            <PriceTextBox
                                valueToFormat="AMOUNT_EXCLUDING_VAT"
                                price={totalQuote}
                                size="S"
                                color={theme.colors.black.light40}
                            />
                        </FlexRow>

                        <FlexRow
                            mainAxis="space-between"
                            padding={{ bottom: 8 }}>
                            <TextBox
                                text={{ sv: "Moms", en: "VAT" }}
                                size="S"
                                color={theme.colors.black.light40}
                            />
                            <PriceTextBox
                                valueToFormat="VAT_AMOUNT"
                                price={totalQuote}
                                size="S"
                                color={theme.colors.black.light40}
                            />
                        </FlexRow>

                        <FlexRow
                            mainAxis="space-between"
                            padding={{ top: 12 }}
                            css={{
                                borderTop: `1px solid ${theme.colors.black.light90.asString}`,
                            }}>
                            <TextBox
                                text={{
                                    sv: "Totalt att betala",
                                    en: "Total to pay",
                                }}
                                size="M"
                                weight="600"
                                color={theme.colors.black.light10}
                            />
                            <PriceTextBox
                                price={totalQuote}
                                size="L"
                                weight="600"
                                color={theme.colors.black.light10}
                            />
                        </FlexRow>
                    </FlexColumn>
                </FlexRow>
            </FlexColumn>
        </FlexColumn>
    );
};

const QuoteAcceptanceSection = () => {
    const theme = useTheme();

    return (
        <FlexColumn
            padding={{ all: 24 }}
            css={{
                backgroundColor: "white",
                borderRadius: 8,
                boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
            }}>
            <FlexColumn crossAxis="center">
                <TextBox
                    text={{
                        sv: "Vill du gå vidare med denna offert?",
                        en: "Would you like to proceed with this quote?",
                    }}
                    size="M"
                    weight="600"
                    color={theme.colors.black.light10}
                />

                <VerticalDivider L />
                <ButtonPrimary
                    css={{ maxWidth: "320px", width: "100%" }}
                    padding={{ topBottom: 12 }}>
                    <Icon icon={faPaperPlane} />
                    <HorizontalDivider S />
                    <TextBox
                        text={{ sv: "Acceptera offert", en: "Accept quote" }}
                        size="M"
                        weight="600"
                        color="white"
                    />
                </ButtonPrimary>

                <VerticalDivider M />

                <TextBox
                    text={{
                        sv: "Genom att acceptera offerten godkänner du även våra allmänna villkor.",
                        en: "By accepting this quote, you also agree to our terms and conditions.",
                    }}
                    size="S"
                    color={theme.colors.black.light40}
                />
            </FlexColumn>
        </FlexColumn>
    );
};

export default QuoteDisplayPage;
